import { useMemo } from 'react';
import type { IdeaTask } from '@/lib/types';
import { differenceInDays, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';

interface CoFounderBriefProps {
  tasks: IdeaTask[];
  timelineEdits: number;
}

interface Signal {
  message: string;
  severity: 'info' | 'warning' | 'concern';
}

export function CoFounderBrief({ tasks, timelineEdits }: CoFounderBriefProps) {
  const signal = useMemo((): Signal | null => {
    const doneTasks = tasks.filter(t => t.status === 'done');
    const doingTasks = tasks.filter(t => t.status === 'doing');
    const blockedTasks = tasks.filter(t => t.status === 'blocked');
    
    // Priority 1: Too many adjustments, no execution
    if (timelineEdits > 10 && doneTasks.length === 0) {
      return {
        message: "You're adjusting the plan more than executing it.",
        severity: 'concern',
      };
    }
    
    // Priority 2: Stalled tasks in Doing
    const stalledTask = doingTasks.find(task => {
      if (!task.start_date) return false;
      const startDate = parseISO(task.start_date);
      const daysSinceStart = differenceInDays(new Date(), startDate);
      return daysSinceStart > 7;
    });
    
    if (stalledTask) {
      return {
        message: "This is stalling. Cut scope or unblock.",
        severity: 'warning',
      };
    }
    
    // Priority 3: Too many blockers
    if (blockedTasks.length > 3) {
      return {
        message: "Too many blockers. Remove one today.",
        severity: 'warning',
      };
    }
    
    // Priority 4: Positive signals
    if (doneTasks.length > 0 && doneTasks.length >= tasks.length * 0.5) {
      return {
        message: "Good momentum. Keep shipping.",
        severity: 'info',
      };
    }
    
    if (doingTasks.length === 1 && blockedTasks.length === 0) {
      return {
        message: "Focused. One thing at a time.",
        severity: 'info',
      };
    }
    
    return null;
  }, [tasks, timelineEdits]);

  if (!signal) return null;

  return (
    <div className={cn(
      "text-xs py-1.5 px-3 rounded-md italic",
      signal.severity === 'concern' && "bg-destructive/10 text-destructive/80",
      signal.severity === 'warning' && "bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400",
      signal.severity === 'info' && "bg-primary/5 text-primary/70"
    )}>
      {signal.message}
    </div>
  );
}